import type { ProjectOrientation } from "../projects/types";
import type { ComicPanel, ComicTemplateId, Rect } from "./comicTypes";

export interface PanelTemplateMetrics {
  margin: number;
  gutter: number;
  borderWidth: number;
}

export function getPanelTemplateMetrics(width: number, height: number): PanelTemplateMetrics {
  const shortSide = Math.min(width, height);
  return {
    margin: Math.min(Math.max(24, Math.round(shortSide * 0.04)), shortSide * 0.12),
    gutter: Math.min(Math.max(16, Math.round(shortSide * 0.018)), shortSide * 0.08),
    borderWidth: Math.min(Math.max(4, Math.round(shortSide * 0.004)), shortSide * 0.025),
  };
}

function splitColumns(area: Rect, columns: number, gutter: number): Rect[] {
  const cellWidth = (area.width - gutter * (columns - 1)) / columns;
  return Array.from({ length: columns }, (_, column) => ({
    x: area.x + column * (cellWidth + gutter),
    y: area.y,
    width: cellWidth,
    height: area.height,
  }));
}

function splitRows(area: Rect, rows: number, gutter: number): Rect[] {
  const cellHeight = (area.height - gutter * (rows - 1)) / rows;
  return Array.from({ length: rows }, (_, row) => ({
    x: area.x,
    y: area.y + row * (cellHeight + gutter),
    width: area.width,
    height: cellHeight,
  }));
}

function grid(area: Rect, columns: number, rows: number, gutter: number): Rect[] {
  return splitRows(area, rows, gutter).flatMap((row) => splitColumns(row, columns, gutter));
}

function templateRects(
  templateId: ComicTemplateId,
  area: Rect,
  orientation: ProjectOrientation,
  gutter: number,
): Rect[] {
  const portraitLike = orientation !== "landscape";
  switch (templateId) {
    case "one-panel":
      return [area];
    case "two-panel":
      return portraitLike ? splitRows(area, 2, gutter) : splitColumns(area, 2, gutter);
    case "three-panel": {
      if (portraitLike) {
        const [top, bottom] = splitRows(area, 2, gutter);
        return top && bottom ? [top, ...splitColumns(bottom, 2, gutter)] : [];
      }
      const [left, right] = splitColumns(area, 2, gutter);
      return left && right ? [left, ...splitRows(right, 2, gutter)] : [];
    }
    case "four-panel":
      return grid(area, 2, 2, gutter);
    case "six-panel":
      return portraitLike ? grid(area, 2, 3, gutter) : grid(area, 3, 2, gutter);
  }
}

export function createPanelTemplate(
  templateId: ComicTemplateId,
  width: number,
  height: number,
  orientation: ProjectOrientation,
  createId: (slot: number) => string = () => crypto.randomUUID(),
): ComicPanel[] {
  const { margin, gutter } = getPanelTemplateMetrics(width, height);
  const area: Rect = {
    x: margin,
    y: margin,
    width: width - margin * 2,
    height: height - margin * 2,
  };
  return templateRects(templateId, area, orientation, gutter).map((rect, index) => ({
    id: createId(index),
    templateSlot: index,
    order: index,
    ...rect,
  }));
}

export function inferTemplateIdFromPanelCount(count: number): ComicTemplateId {
  if (count === 2) return "two-panel";
  if (count === 3) return "three-panel";
  if (count === 4) return "four-panel";
  if (count === 6) return "six-panel";
  return "one-panel";
}
